import { useQueryClient } from "@tanstack/react-query";
import { UseKlydoMutationOptions } from "../../../components/hooks";

import useKlydoMutation from "../../../components/hooks/useKlydoMutation";
import uploadsApiClient, {
  UploadFileInput,
  UploadResponse,
} from "../../../api/uploads";

const useUploadKlydoLoopMutation = (
  options: Omit<
    UseKlydoMutationOptions<
      UploadResponse,
      UploadFileInput,
      { previousUploadStatus: UploadResponse | undefined }
    >,
    "onSuccess" | "onSettled"
  > & {
    onSuccess?: (response: UploadResponse, variables: UploadFileInput) => void;
    onSettled?: (response: UploadResponse | undefined, error: unknown) => void;
  } = {},
) => {
  const queryClient = useQueryClient();

  return useKlydoMutation({
    mutationFn: (input: UploadFileInput) =>
      uploadsApiClient.uploadKlydoLoop(input),
    mutationKey: ["uploadKlydoLoop"],
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ["uploadKlydoLoop"] });

      const previousUploadStatus: UploadResponse | undefined =
        queryClient.getQueryData(["uploadKlydoLoop"]);

      options.onMutate?.(input);

      return { previousUploadStatus };
    },
    onError: (error, input, context) => {
      if (context?.previousUploadStatus) {
        queryClient.setQueryData(
          ["uploadKlydoLoop"],
          context.previousUploadStatus,
        );
      }
      options.onError?.(error, input, context);
    },
    onSettled: async (response, error) => {
      if (options.onSettled) {
        options.onSettled(response, error);
      }
      await queryClient.invalidateQueries({ queryKey: ["uploadKlydoLoop"] });
    },
    onSuccess: (response, variables) => {
      if (options.onSuccess) {
        options.onSuccess(response, variables);
      }
    },
    ...options,
  });
};

export default useUploadKlydoLoopMutation;
